/**
 * Forensic Engine
 * 
 * Analyzes verdict data and generates forensic insights with
 * "tough love" roast copy from the language library.
 * 
 * Tone: 7/10 intensity - Direct, evidence-based, memorable,
 * compassionate about the artist's struggle.
 */

import { VerdictInputs, VerdictCalculations, VerdictFlags, ForensicInsight, InsightMetric, Verdict } from '@/types';
import { TIERS } from '@/types/tier';
import { getSimilarWorkPrices, checkConsistency, buildConsistencyInsight } from './consistency';

/**
 * Format cents to currency string
 */
function formatCurrency(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  return sign + new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(cents) / 100);
}

/**
 * Format hours with 1 decimal place
 */
function formatHours(hours: number): string {
  return hours.toFixed(1);
}

/**
 * Format percentage as whole number
 */
function formatPercent(ratio: number): string {
  return Math.round(ratio * 100) + '%';
}

/**
 * Generate all applicable forensic insights for a verdict
 */
export function generateForensicInsights(
  inputs: VerdictInputs,
  calculations: VerdictCalculations,
  flags: VerdictFlags,
  existingVerdicts?: Verdict[],
  excludeVerdictId?: string,
): ForensicInsight[] {
  const insights: ForensicInsight[] = [];
  
  // Gate 1: The Charity Subsidy (Delta < 0)
  if (calculations.delta !== null && calculations.delta < 0) {
    const hoursDonated = Math.abs(calculations.delta) / (calculations.survivalTarget || 1);
    const dataLine = `Your price (${formatCurrency(inputs.gutPrice!)}) is ${formatCurrency(Math.abs(calculations.delta))} below your breakeven floor (${formatCurrency(calculations.ttcp)}). That gap equals ${formatHours(hoursDonated)} hours of unpaid labor.`;
    const meaningLine = 'At this price, you are subsidizing the buyer — paying to give your work away.';
    const checkLine = 'Is this price intentional (loss-leader, gift)? If not, the price or the hours need adjustment.';

    insights.push({
      key: 'charity_subsidy',
      severity: 'critical',
      title: 'The Charity Subsidy',
      icon: '💸',
      body: `${dataLine} ${meaningLine}`,
      parts: { data: dataLine, meaning: meaningLine, check: checkLine },
      metrics: [
        { label: 'Hours donated', value: formatHours(hoursDonated), highlight: true },
        { label: 'Value lost', value: formatCurrency(Math.abs(calculations.delta)), highlight: true },
      ],
      isPremium: false,
    });
  }
  
  // Gate 2: The Material Reseller (Materials > 40% of gut price)
  if (inputs.gutPrice && inputs.gutPrice > 0) {
    const materialRatio = inputs.materials / inputs.gutPrice;
    if (materialRatio > 0.40) {
      insights.push({
        key: 'material_reseller',
        severity: 'warning',
        title: 'High Material Ratio',
        icon: '🎨',
        body: `Materials (${formatCurrency(inputs.materials)}) are ${formatPercent(materialRatio)} of your price (${formatCurrency(inputs.gutPrice!)}). Threshold: 40%. Material costs are consuming a disproportionate share of your revenue.`,
        parts: {
          data: `Materials (${formatCurrency(inputs.materials)}) are ${formatPercent(materialRatio)} of your price (${formatCurrency(inputs.gutPrice!)}). Threshold: 40%.`,
          meaning: 'Material costs are consuming a disproportionate share of your revenue.',
          check: 'Can materials be sourced more economically? Is the price too low for this medium?',
        },
        metrics: [
          { label: 'Material cost', value: formatCurrency(inputs.materials) },
          { label: 'Your asking price', value: formatCurrency(inputs.gutPrice) },
          { label: 'Material ratio', value: formatPercent(materialRatio), highlight: true },
        ],
        isPremium: false,
      });
    }
  }
  
  // Gate 3: Minimum Wage Alert (Net hourly < $7.25)
  // Uses net EH (after materials) — checks actual take-home against wage floor
  const FEDERAL_MIN_WAGE_CENTS = 725; // $7.25/hr in cents
  const netEffectiveHourly = (inputs.gutPrice !== null && inputs.gutPrice > 0 && inputs.hours > 0)
    ? (inputs.gutPrice - inputs.materials) / inputs.hours
    : null;
  if (netEffectiveHourly !== null && netEffectiveHourly < FEDERAL_MIN_WAGE_CENTS) {
    insights.push({
      key: 'minimum_wage_alert',
      severity: 'critical',
      title: 'Below Minimum Wage',
      icon: '⚠️',
      body: `Your net rate is ${formatCurrency(netEffectiveHourly)}/hr (federal minimum: $7.25/hr). At this price, your hourly earnings fall below legal minimum wage.`,
      parts: {
        data: `Your net rate is ${formatCurrency(netEffectiveHourly)}/hr (federal minimum: $7.25/hr).`,
        meaning: 'At this price, your hourly earnings fall below legal minimum wage.',
        check: 'Are the hours accurate? Are material costs overstated?',
      },
      metrics: [
        { label: 'Net Hourly (after materials)', value: formatCurrency(netEffectiveHourly) + '/hr', highlight: true },
        { label: 'Federal minimum', value: '$7.25/hr' },
        { label: 'Your survival target', value: formatCurrency(calculations.survivalTarget) + '/hr' },
      ],
      isPremium: false,
    });
  }
  
  // Gate 4: Over-Density (Positive - working harder than tier suggests)
  if (flags.isOverDensity && !flags.isSpeedVeto) {
    const tierConfig = TIERS[inputs.tier];
    insights.push({
      key: 'over_density',
      severity: 'positive',
      title: 'Exceptional Detail',
      icon: '✨',
      body: `Density ${calculations.density.toFixed(3)} h/sq in exceeds the ${tierConfig.label} tier maximum (${tierConfig.densityMax.toFixed(3)} h/sq in). Labor investment per square inch is above the expected range.`,
      parts: {
        data: `Density ${calculations.density.toFixed(3)} h/sq in exceeds the ${tierConfig.label} tier maximum (${tierConfig.densityMax.toFixed(3)} h/sq in).`,
        meaning: 'Labor investment per square inch is above the expected range for this tier.',
        check: 'Is this intentional detail work? If so, the ceiling may undervalue the labor.',
      },
      metrics: [
        { label: 'Your tier', value: tierConfig.label },
        { label: 'Expected density', value: `${tierConfig.densityMin.toFixed(3)}–${tierConfig.densityMax.toFixed(3)} h/sqin` },
        { label: 'Actual density', value: `${calculations.density.toFixed(3)} h/sqin`, highlight: true },
      ],
      isPremium: false,
    });
  }
  
  // Gate 5: Speed Veto Warning (Under-density)
  if (flags.isSpeedVeto && !flags.masteryOverrideApplied) {
    const tierConfig = TIERS[inputs.tier];
    insights.push({
      key: 'under_density',
      severity: 'warning',
      title: 'Credibility Check',
      icon: '⏱️',
      body: `Density ${calculations.density.toFixed(3)} h/sq in is below the ${tierConfig.label} minimum (${tierConfig.densityMin.toFixed(3)} h/sq in). The claimed effort level does not match labor density.`,
      parts: {
        data: `Density ${calculations.density.toFixed(3)} h/sq in is below the ${tierConfig.label} minimum (${tierConfig.densityMin.toFixed(3)} h/sq in).`,
        meaning: 'The claimed effort level does not match labor density.',
        check: 'Is the effort level correct? Were hours tracked accurately?',
      },
      metrics: [
        { label: 'Selected tier', value: tierConfig.label },
        { label: 'Minimum expected', value: `${tierConfig.densityMin.toFixed(3)} h/sqin` },
        { label: 'Your density', value: `${calculations.density.toFixed(3)} h/sqin`, highlight: true },
        ...(flags.suggestedTier ? [{ label: 'Suggested tier', value: TIERS[flags.suggestedTier].label, highlight: true }] : []),
      ],
      isPremium: false,
    });
  }
  
  // Gate 6: Ceiling Below Floor Warning
  if (flags.isCeilingBelowFloor) {
    insights.push({
      key: 'ceiling_warning',
      severity: 'warning',
      title: 'Market High Alert',
      icon: '📊',
      body: `Your breakeven floor (${formatCurrency(calculations.ttcp)}) exceeds the market ceiling (${formatCurrency(calculations.ceiling)}). The market may not support this cost at this size and career stage.`,
      parts: {
        data: `Breakeven floor: ${formatCurrency(calculations.ttcp)}. Market ceiling: ${formatCurrency(calculations.ceiling)}. Gap: ${formatCurrency(calculations.ttcp - calculations.ceiling)}.`,
        meaning: 'The market may not support this cost at this size and career stage.',
        check: 'Can hours or materials be reduced? Is a different medium or size more viable?',
      },
      metrics: [
        { label: 'Your floor', value: formatCurrency(calculations.ttcp) },
        { label: 'Market ceiling', value: formatCurrency(calculations.ceiling), highlight: true },
        { label: 'Gap', value: formatCurrency(calculations.ttcp - calculations.ceiling) },
      ],
      isPremium: false,
    });
  }
  
  // Gate 7: Small Work Premium (Positive)
  if (flags.detailPremiumApplied) {
    insights.push({
      key: 'small_work_premium',
      severity: 'positive',
      title: 'Detail Premium Applied',
      icon: '🔍',
      body: `Canvas area ${calculations.area} sq in at ${calculations.detailPremium.toFixed(2)}\u00D7 detail premium. Small works at high effort levels receive an upward ceiling adjustment.`,
      parts: {
        data: `Canvas area ${calculations.area} sq in. Detail premium: ${calculations.detailPremium.toFixed(2)}\u00D7.`,
        meaning: 'Small works at high effort levels receive an upward ceiling adjustment for concentrated craftsmanship.',
        check: 'Is the effort level accurate for this piece?',
      },
      metrics: [
        { label: 'Canvas area', value: `${calculations.area} sq in` },
        { label: 'Detail premium', value: `${calculations.detailPremium.toFixed(2)}×`, highlight: true },
      ],
      isPremium: false,
    });
  }
  
  // Gate 8: Mastery Override (Info)
  if (flags.masteryOverrideApplied) {
    insights.push({
      key: 'mastery_override',
      severity: 'info',
      title: 'Mastery Override',
      icon: '👑',
      body: `Career stage: Established. Density veto bypassed. Track record at this career stage allows flexibility in tier selection.`,
      parts: {
        data: 'Career stage: Established. Density veto bypassed.',
        meaning: 'Track record at this career stage allows flexibility in tier selection.',
        check: 'Is the career stage current and accurate?',
      },
      metrics: [
        { label: 'Career stage', value: 'Established' },
        { label: 'Override applied', value: 'Yes', highlight: true },
      ],
      isPremium: false, // Show this one for free as it's informational
    });
  }

  // NEW INSIGHTS FROM PROTOTYPE INTEGRATION

  // Gate 9: Hourly Reality (Effective hourly vs survival target gap)
  if (calculations.effectiveHourly !== null && calculations.survivalTarget !== null) {
    const hourlyGap = calculations.survivalTarget - calculations.effectiveHourly;
    if (hourlyGap > calculations.survivalTarget * 0.3) {
      insights.push({
        key: 'hourly_reality',
        severity: 'warning',
        title: 'Hourly Reality',
        icon: '⏰',
        body: `Effective rate: ${formatCurrency(calculations.effectiveHourly)}/hr. Survival target: ${formatCurrency(calculations.survivalTarget)}/hr. Your hourly rate does not cover your survival threshold.`,
        parts: {
          data: `Effective rate: ${formatCurrency(calculations.effectiveHourly)}/hr. Survival target: ${formatCurrency(calculations.survivalTarget)}/hr.`,
          meaning: 'Your hourly rate does not cover your survival threshold.',
          check: 'Is the asking price too low, or are the hours too high for this piece?',
        },
        metrics: [
          { label: 'Current rate', value: formatCurrency(calculations.effectiveHourly) + '/hr', highlight: true },
          { label: 'Target rate', value: formatCurrency(calculations.survivalTarget) + '/hr' },
        ],
        isPremium: false,
      });
    }
  }

  // Gate 10: Dead-End Studio (Overhead > 60% of revenue)
  const gutPrice = inputs.gutPrice || calculations.ttcp;
  if (inputs.materials > 0 && gutPrice > 0) {
    const overheadRatio = inputs.materials / gutPrice;
    if (overheadRatio > 0.6) {
      insights.push({
        key: 'dead_end_studio',
        severity: 'critical',
        title: 'Dead-End Studio',
        icon: '🏚️',
        body: `Materials (${formatCurrency(inputs.materials)}) are ${formatPercent(overheadRatio)} of revenue (${formatCurrency(gutPrice)}). At this ratio, the majority of revenue covers supplies before labor is paid.`,
        parts: {
          data: `Materials: ${formatCurrency(inputs.materials)}. Revenue: ${formatCurrency(gutPrice)}. Overhead ratio: ${formatPercent(overheadRatio)}.`,
          meaning: 'At this ratio, the majority of revenue covers supplies before labor is paid.',
          check: 'Can material costs be reduced? Is the piece priced high enough for this medium?',
        },
        metrics: [
          { label: 'Material costs', value: formatCurrency(inputs.materials) },
          { label: 'Your price', value: formatCurrency(gutPrice) },
          { label: 'Overhead ratio', value: formatPercent(overheadRatio), highlight: true },
        ],
        isPremium: false,
      });
    }
  }

  // Gate 11: Time Bandit (Estimated hours - low data quality)
  const isEstimated = inputs.hoursTracked === false;
  if (isEstimated && inputs.hours > 20) {
    insights.push({
      key: 'time_bandit',
      severity: 'warning',
      title: 'Time Bandit',
      icon: '🕵️',
      body: `${formatHours(inputs.hours)} hours estimated (not tracked). Estimated time introduces error into the floor calculation.`,
      parts: {
        data: `${formatHours(inputs.hours)} hours estimated (not tracked).`,
        meaning: 'Estimated time introduces error into the floor calculation.',
        check: 'Can hours be tracked on the next piece? Is the estimate conservative or generous?',
      },
      metrics: [
        { label: 'Estimated hours', value: formatHours(inputs.hours), highlight: true },
        { label: 'Tracking status', value: 'Estimated (not tracked)' },
      ],
      isPremium: false,
    });
  }

  // Gate 12: Scope Mirage (High hours but low tier)
  const tierIndex = ['L1', 'L2', 'L3', 'L4', 'L5'].indexOf(inputs.tier);
  if (inputs.hours >= 40 && tierIndex >= 0 && tierIndex < 2) {
    insights.push({
      key: 'scope_mirage',
      severity: 'warning',
      title: 'Scope Mirage',
      icon: '🎭',
      body: `${formatHours(inputs.hours)} hours at ${TIERS[inputs.tier].label} fidelity. Expected range for this tier: <40 hours. High hours at low complexity indicates padded time or misclassified fidelity.`,
      parts: {
        data: `${formatHours(inputs.hours)} hours at ${TIERS[inputs.tier].label} fidelity. Expected range for this tier: <40 hours.`,
        meaning: 'High hours at low complexity indicates padded time or misclassified fidelity.',
        check: 'Is the effort level correct? Were hours tracked accurately?',
      },
      metrics: [
        { label: 'Hours', value: formatHours(inputs.hours), highlight: true },
        { label: 'Tier', value: TIERS[inputs.tier].label },
      ],
      isPremium: false,
    });
  }

  // Gate 13: Consistency Warning (price deviates >25% from similar work history)
  if (existingVerdicts && existingVerdicts.length > 0) {
    const area = inputs.width * inputs.height;
    if (area > 0) {
      const similarPrices = getSimilarWorkPrices(existingVerdicts, inputs.medium, area, excludeVerdictId);
      const newPrice = inputs.gutPrice ?? calculations.ttcp;
      const priceSource = inputs.gutPrice ? 'gutPrice' as const : 'ttcp' as const;
      if (newPrice > 0) {
        const result = checkConsistency(similarPrices, newPrice, priceSource);
        if (result) {
          insights.push(buildConsistencyInsight(result, inputs.medium));
        }
      }
    }
  }

  return insights;
}

/**
 * Get the most critical insight (for teaser display)
 */
export function getPrimaryInsight(insights: ForensicInsight[]): ForensicInsight | null {
  // Priority: critical > warning > info > positive
  const severityOrder = ['critical', 'warning', 'info', 'positive'];
  
  for (const severity of severityOrder) {
    const insight = insights.find(i => i.severity === severity);
    if (insight) return insight;
  }
  
  return insights[0] || null;
}

/**
 * Count premium-gated insights
 */
export function countPremiumInsights(insights: ForensicInsight[]): number {
  return insights.filter(i => i.isPremium).length;
}
